import React, { useContext, useEffect, useState, useId, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { useSearchParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Typography } from 'design-system/src/components/Typography/Typography';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import TabList from 'design-system/src/components/TabList';
import NotificationDot from 'design-system/src/components/NotificationDot/NotificationDot';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';

import { AuthContext } from '../auth';
import { client, type OnboardingInfoResponse } from '../api';
import PageContainer from '../components/PageContainer';
import NotificationSettings, { NotificationSettingsHandle } from '../components/settings/NotificationSettings';
import UsersTable from '../components/settings/UsersTable';
import Profile from '../components/settings/Profile';
import OfficePhoneNumbers from '../components/settings/OfficePhoneNumbers';
import Offices from '../components/settings/Offices';
import { Table, TableCell, TableHeader } from '../components/Table';
import { PROVIDER_SUPPORT_CONTACT_INFO } from '../constants';
import { createTrackingProps } from 'shared-utils/analytics';
import { EhrIntegrationForm } from '../components/onboarding/EhrIntegrationForm';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { getEhrIntegrationConfig } from '../components/tasks/EhrIntegrationConfig';
import { isValidEhr } from '../utils';
import BenefitPreferences from '../components/settings/BenefitPreferences';
import ProvidersTable from '../components/settings/ProvidersTable/ProvidersTable';

type Tab =
  | 'notifications'
  | 'users'
  | 'profile'
  | 'config'
  | 'officeNumbers'
  | 'offices'
  | 'providers'
  | 'ehrIntegration'
  | 'benefitPreferences';

type ConfigData = {
  show_config: boolean;
  icd_mapping: Record<string, string>;
};

const SettingsTabs = styled(TabList)`
  margin: 20px 0;
`;

const HelperText = styled(Typography)`
  padding-left: 0.5rem;
`;

// TODO(design-system): Replace with Link component
const Link = styled.a`
  color: var(--grayscale-600);
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid var(--grayscale-200);
  padding-bottom: 2px;
`;

const ConfigContainer = styled.div`
  max-width: 40em;
`;

const StyledTabDescription = styled.div``;

const TabDescriptionDivider = styled.div`
  width: 20px;
  height: 1px;
  background-color: var(--grayscale-300);
  margin-top: 20px;
  margin-bottom: 20px;
`;

const StickyFooter = styled.div`
  position: sticky;
  bottom: 0;
  padding: 16px 0;
  width: 100%;
  background-color: var(--surface-50);
`;

const ConfigView: React.FC<{ data: ConfigData }> = ({ data }) => {
  const { icd_mapping } = data;
  return (
    <ConfigContainer>
      <HelperText styledAs="bodyDMSans" marginBottom="1rem">
        Below are your account settings for default ICD-10 codes to use on prior authorization requests for specific
        medications, unless otherwise specified in clinical documentation.
        <br />
        <br />
        You can <Link href={`mailto:${PROVIDER_SUPPORT_CONTACT_INFO.email}`}>contact support</Link> at any time to
        update these settings.
      </HelperText>
      <Table>
        <thead>
          <tr>
            <TableHeader>Drug Name</TableHeader>
            <TableHeader>ICD-10 Code</TableHeader>
          </tr>
        </thead>
        <tbody>
          {Object.entries(icd_mapping).map(([ingredient, code]) => (
            <tr key={ingredient}>
              <TableCell>{ingredient}</TableCell>
              <TableCell>{code}</TableCell>
            </tr>
          ))}
        </tbody>
      </Table>
    </ConfigContainer>
  );
};

const TabDescription: React.FC<{ tab: Tab }> = ({ tab }) => {
  if (tab === 'providers') {
    return (
      <StyledTabDescription>
        <Typography styledAs="bodySmallDMSans">
          Users will be able to customize their default{' '}
          <Link href="/settings?tab=notifications">Notifications & Filters</Link> based on the providers added here.
        </Typography>
        <TabDescriptionDivider />
      </StyledTabDescription>
    );
  }

  if (tab !== 'notifications') {
    return null;
  }

  return (
    <StyledTabDescription>
      <Typography styledAs="bodySmallDMSans" weight={600}>
        Customize your portal experience
      </Typography>
      <Typography styledAs="bodySmallDMSans">
        Set which providers' prescriptions are shown by default in the portal and which ones you receive email alerts
        for.
      </Typography>
      <TabDescriptionDivider />
    </StyledTabDescription>
  );
};

const SettingsPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEhrIntegrationSubmitted, setIsEhrIntegrationSubmitted] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabInState, setTabInState] = useState<Tab>((searchParams.get('tab') as Tab) || 'notifications');
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const tabListId = useId();
  const tabPanelId = useId();
  const settingsRef = useRef<NotificationSettingsHandle>(null);
  const queryClient = useQueryClient();

  const showEhrUpsells = authContext.user?.feature_flags?.show_ehr_upsells;
  const hasEhrIntegrationSettingsTab = authContext.user?.has_ehr_integration_settings_tab;
  const practiceName = authContext.user?.practice_name || '';
  const practiceEhrType =
    authContext.user?.ehr_name && isValidEhr(authContext.user.ehr_name) ? authContext.user.ehr_name : null;
  const ehrTypeForIntegrationForm = hasEhrIntegrationSettingsTab ? practiceEhrType : null;
  const isAdmin = authContext.user?.is_admin;
  const hasPadPreferences = authContext.user?.feature_flags?.has_pad_preferences;

  // if the user switches tabs using the link in the NavSidebar after the component has mounted,
  // we need to update the tab in state to match the new tab in the url
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab) {
      setTabInState(tab as Tab);
    }
  }, [location]);

  const setTab = React.useCallback(
    (tab: Tab) => {
      setTabInState(tab);
      setSearchParams({ tab });
    },
    [setSearchParams],
  );

  const configQuery = useQuery<ConfigData>({
    queryKey: ['config'],
    queryFn: () => {
      return client.get(`config`).then((res) => res.data);
    },
  });

  const onboardingInfoQuery = useQuery<OnboardingInfoResponse>({
    queryKey: ['onboardingInfo'],
    queryFn: () => {
      return client.get('onboarding/info').then((res) => res.data);
    },
    enabled: hasEhrIntegrationSettingsTab && !!ehrTypeForIntegrationForm,
  });

  const ehrIntegrationSubmit = useMutation<void, Error, Record<string, string>>({
    mutationFn: async (credentials: Record<string, string>) => {
      if (!ehrTypeForIntegrationForm) {
        throw new Error('EHR type is required');
      }
      await client.post('/onboarding/ehr-integration', {
        ehr_name: ehrTypeForIntegrationForm,
        credentials,
        source: 'settings_page',
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['onboardingInfo'] });
      setIsEhrIntegrationSubmitted(true);
      if (ehrTypeForIntegrationForm) {
        const config = getEhrIntegrationConfig(ehrTypeForIntegrationForm, practiceName);
        toast.custom(() => <Toast variant="success">{config.getSuccessMessage()}</Toast>);
      }
    },
    onError: (error) => {
      console.error(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  // Normalize tabInState against available tabs and fall back to 'notifications' if invalid
  useEffect(() => {
    if (configQuery.isLoading || !configQuery.data) {
      return;
    }

    const showConfig = configQuery.data?.show_config;

    const availableTabs = [
      'notifications',
      'users',
      'officeNumbers',
      'offices',
      ...(isAdmin ? ['providers'] : []),
      'profile',
      ...(hasPadPreferences ? ['benefitPreferences'] : []),
      ...(showConfig ? ['config'] : []),
      ...(hasEhrIntegrationSettingsTab && ehrTypeForIntegrationForm ? ['ehrIntegration'] : []),
    ];

    if (!availableTabs.includes(tabInState)) {
      setTab('notifications');
    }
  }, [
    tabInState,
    setTab,
    configQuery.data,
    configQuery.isLoading,
    hasEhrIntegrationSettingsTab,
    ehrTypeForIntegrationForm,
    hasPadPreferences,
    isAdmin,
  ]);

  let body;
  let memberId: string | undefined;

  if (configQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (configQuery.error) {
    body = <PageLoadError />;
  } else if (!configQuery.data) {
    body = null;
  } else {
    const showConfig = configQuery.data.show_config;
    memberId = authContext.user?.member_id;

    const tabs = [
      { value: 'notifications', label: 'Notifications & Filters' },
      { value: 'users', label: 'Users' },
      { value: 'officeNumbers', label: 'Office numbers' },
      { value: 'offices', label: 'Offices' },
      ...(isAdmin ? [{ value: 'providers', label: 'Providers' }] : []),
      { value: 'profile', label: 'Profile' },
      ...(hasPadPreferences ? [{ value: 'benefitPreferences', label: 'Provider-administered drugs' }] : []),
      ...(showConfig ? [{ value: 'config', label: 'Other Configurations' }] : []),
      ...(hasEhrIntegrationSettingsTab && ehrTypeForIntegrationForm
        ? [
            {
              value: 'ehrIntegration',
              label: showEhrUpsells ? <NotificationDot>EHR Connection</NotificationDot> : 'EHR Connection',
            },
          ]
        : []),
    ];

    const notificationSettings = memberId ? (
      <>
        <NotificationSettings memberId={memberId} ref={settingsRef} onSubmittingChange={setIsSubmitting} />
        <StickyFooter>
          <ButtonRow align={Alignment.Left} fullWidth>
            <Button size="sm" onClick={() => settingsRef.current?.save()} isLoading={isSubmitting}>
              Save changes
            </Button>
          </ButtonRow>
        </StickyFooter>
      </>
    ) : null;

    body = (
      <>
        <SettingsTabs
          tabs={tabs}
          selectedValue={tabInState}
          onSelect={(value) => setTab(value as Tab)}
          tabListId={tabListId}
          tabPanelId={tabPanelId}
          ariaLabel="Settings navigation"
          variant="fullWidthWithBorder"
          {...createTrackingProps({ elementName: 'Settings Page: Tabs', unmask: true })}
        />

        <div role="tabpanel" id={tabPanelId} aria-labelledby={tabListId}>
          <TabDescription tab={tabInState} />

          {tabInState === 'notifications' && notificationSettings}
          {tabInState === 'users' && <UsersTable isViewOnly={!isAdmin} />}
          {tabInState === 'officeNumbers' && <OfficePhoneNumbers />}
          {tabInState === 'offices' && <Offices />}
          {tabInState === 'providers' && <ProvidersTable />}
          {tabInState === 'profile' && <Profile />}
          {tabInState === 'benefitPreferences' && <BenefitPreferences />}
          {tabInState === 'config' && showConfig && <ConfigView data={configQuery.data} />}
          {tabInState === 'ehrIntegration' && ehrTypeForIntegrationForm && (
            <EhrIntegrationForm
              ehrType={ehrTypeForIntegrationForm}
              practiceName={practiceName}
              onSubmit={(credentials) => ehrIntegrationSubmit.mutate(credentials)}
              isSubmitLoading={ehrIntegrationSubmit.isPending}
              isCompleted={
                onboardingInfoQuery.data?.practice_statuses.ehr_integration_status === 'credentials_verified'
              }
              isSubmitted={isEhrIntegrationSubmitted}
              source="settingsPage"
            />
          )}
        </div>
      </>
    );
  }

  return (
    <PageContainer title="Settings" width="fitContent">
      {body}
    </PageContainer>
  );
};

export default SettingsPage;
